import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ModuleUnavailable,
  NOT_ENTITLED,
  SessionExpired,
  StepUpRequired,
  get,
} from './api'
import { useStepUpGeneration } from './StepUpGate'

// ======================================================================
// THE 404 RULE, AS A HOOK
// ======================================================================
//
// `entitled: false` is what a 404 becomes. The caller renders NOTHING —
// not an error, not an empty card with a padlock, not a tab that is
// present but greyed. er_cases/acl.py made every refusal identical
// specifically so the UI would have nothing to leak, and a section that
// appears in a disabled state hands the difference back: it says "this
// exists and is being kept from you", which is the 403 the whole module
// avoids.
//
// `enabled: false` is different and is about NOT ASKING. Every refused
// read writes a durable access_denied row to the case's hash chain
// (acl._log_denied, out of band, so it survives even when the request
// rolls back). A UI that fires every endpoint at every case and lets the
// 404s sort it out would fill the subject's own audit trail with denials
// the subject never caused — noise in the one record that is supposed to
// be legible. So sections are asked for only when the CaseDetail
// response says the caller holds the capability that section's endpoint
// requires. CaseDetail echoes `capabilities` for exactly this purpose.
//
// The capability check is a courtesy to the audit trail, never the
// security boundary. The boundary is require_case on the server, which
// runs regardless.
//
// ======================================================================
// TWO ERRORS ARE DELIBERATELY NOT ERRORS HERE (task 11B)
// ======================================================================
//
// StepUpRequired and ModuleUnavailable are both handled ABOVE this hook,
// by StepUpGate, which covers the screen. Setting `error` on them as
// well would draw a red refusal box inside every section at the same
// moment — a dozen alarming messages behind a prompt that is already
// explaining the one real cause. So they are swallowed here exactly as
// SessionExpired is, and for the same reason: something else is already
// dealing with it.
//
// `generation` is the counter StepUpGate increments on each successful
// verification. Reads refused while a marker was dead reload when a new
// one is obtained, without anything being unmounted — so a half-typed
// working note survives a re-prompt and the sections around it still
// come back.

export function useResource(path, { enabled = true, deps = [] } = {}) {
  const generation = useStepUpGeneration()
  const [state, setState] = useState({
    data: null,
    entitled: true,
    loading: enabled,
    error: null,
  })
  const seq = useRef(0)

  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!enabled || !path) {
        setState({ data: null, entitled: false, loading: false, error: null })
        return
      }
      const mine = ++seq.current
      // STALE WHILE REVALIDATING, AND THIS IS NOT A PERFORMANCE CHOICE.
      //
      // A REFETCH MUST NOT BLANK THE SCREEN. CaseWorkspace returns a
      // spinner INSTEAD of the workspace while the case detail is
      // loading, so flipping `loading` on a reload unmounts every
      // section, every open dialog and everything typed into one.
      //
      // Which is exactly what a step-up re-prompt does: verifying bumps
      // `generation`, every resource reloads, and the case owner who was
      // half way through typing a reason for a pause got the spinner and
      // lost it — the precise failure the gate's overlay was built to
      // avoid, reintroduced one layer down. Found by test 5, not by
      // reading.
      //
      // So a reload with data already held keeps showing it and swaps it
      // when the answer arrives. Only a FIRST load shows a spinner.
      if (!quiet) {
        setState((s) =>
          s.data == null
            ? { ...s, loading: true, error: null }
            : { ...s, error: null },
        )
      }
      try {
        const res = await get(path)
        if (mine !== seq.current) return
        if (res === NOT_ENTITLED) {
          setState({ data: null, entitled: false, loading: false, error: null })
        } else {
          setState({ data: res, entitled: true, loading: false, error: null })
        }
      } catch (e) {
        if (mine !== seq.current) return
        if (
          e instanceof SessionExpired ||
          e instanceof StepUpRequired ||
          e instanceof ModuleUnavailable
        ) {
          // Handled above. Left in `loading` so the section renders
          // nothing rather than an empty state that would read as "there
          // is none of this" when the truth is "we have not asked yet".
          return
        }
        setState({ data: null, entitled: true, loading: false, error: e })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path, enabled, generation, ...deps],
  )

  useEffect(() => {
    load()
  }, [load])

  /** Replace the held data outright. Every write route on this router
   *  returns the FULL refreshed collection — routes.py does that on
   *  purpose, so a client never needs a second round trip and never
   *  sees a POST and a GET disagree. Using the response instead of
   *  refetching is therefore both cheaper and strictly more consistent. */
  const replace = useCallback((next) => {
    seq.current += 1
    setState({ data: next, entitled: true, loading: false, error: null })
  }, [])

  return { ...state, reload: load, replace }
}
